import React, { useRef } from 'react';
import './modernDatePicker.scss';

interface ModernDatePickerProps {
  label?: string;
  labelAlign?: 'left' | 'center';
  value: Date | string | null;
  onChange: (date: Date | null) => void;
  placeholder?: string;
  error?: string;
  fullWidth?: boolean;
  className?: string;
  disabled?: boolean;
  required?: boolean;
}

// timezone-safe: a "YYYY-MM-DD" string is a calendar date with no zone. `new Date(str)`
// would parse it as UTC midnight and render the prior day west of UTC, so read the
// date parts off the string directly and only use local getters for real Date values.
const toInputDateString = (value: Date | string | null): string => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return value.split('T')[0];
  }

  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
};

const ModernDatePicker = ({
  label,
  labelAlign = 'center',
  value,
  onChange,
  placeholder = 'Select date',
  error,
  fullWidth = true,
  className = '',
  disabled = false,
  required = false,
}: ModernDatePickerProps) => {
  const inputRef: React.RefObject<HTMLInputElement> = useRef<HTMLInputElement>(null);

  const formattedValue: string = toInputDateString(value);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value) {
      const [year, month, day] = e.target.value.split('-').map(Number);
      onChange(new Date(year, month - 1, day));
    } else {
      onChange(null);
    }
  };

  const handleWrapperClick = () => {
    if (inputRef.current && !disabled) {
      if ('showPicker' in HTMLInputElement.prototype) {
        inputRef.current.showPicker();
      } else {
        inputRef.current.click();
      }
    }
  };

  return (
    <div
      className={`modern-date-picker-container ${fullWidth ? 'full-width' : ''} ${className}`}
    >
      {label && (
        <label
          className="modern-date-picker-label"
          style={{
            textAlign: labelAlign,
            alignSelf: labelAlign === 'left' ? 'flex-start' : 'center',
            width: '100%',
            fontWeight: 600
          }}
        >
          {label}
          {required && (
            <span className="modern-date-picker-required">*</span>
          )}
        </label>
      )}
      <div
        className="modern-date-picker-field-wrapper"
        onClick={handleWrapperClick}
        style={{ cursor: disabled ? 'default' : 'text' }}
      >
        <div className="modern-date-picker-icon">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
        </div>
        <input
          ref={inputRef}
          type="date"
          className={`modern-date-picker-field ${error ? 'error' : ''}`}
          value={formattedValue}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
        />
      </div>
      {error && <div className="modern-date-picker-error">{error}</div>}
    </div>
  );
};

export default ModernDatePicker;